import {Button, FormControlLabel, Stack, Switch, type SelectChangeEvent} from '@mui/material';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import {CompanySelector} from 'components/Shared/CompanySelector';
import {useInputMenuContext} from 'context/InputMenu';
import {useGetLiveFishTransaction, useGetSiteCages, useGetSites} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {type IFormData, inputMenuActionType, methodType, SWRKeyEnum} from 'interfaces';
import {useTranslation} from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {type FormEvent, useState} from 'react';
import Alert from 'helpers/Alert';
import backendCommunicationInstance from 'services/backendCommunicator';
import {dateReader, localDateSender} from 'helpers/UnitsFormatHandler';
import {mutate} from 'swr';
import {type LiveFishTransactionResponse} from 'client';
import {mutateAll} from 'hooks/swr/swrHookFactory';
import {useGetAvailableFishGroup} from 'hooks/useGetAvailableFishGroup';

export const LiveFishTransactionsForm = () => {
  const {t} = useTranslation('common');
  const {planId} = useParseURL();
  const inputMenuContext = useInputMenuContext();
  const {data: sites} = useGetSites({resourceId: planId});
  const {data: liveFishTransaction, isLoading: isLiveFishTransactionLoading} = useGetLiveFishTransaction({
    resourceId: inputMenuContext?.state?.formData?.id ? [planId, inputMenuContext.state.formData.id] : undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = isSubmitting || isLiveFishTransactionLoading;

  const initialFormData = {
    fromCompanyId: liveFishTransaction?.fromCompanyId,
    toCompanyId: liveFishTransaction?.toCompanyId,
    siteId: liveFishTransaction?.siteId,
    cageId: liveFishTransaction?.cageId,
    date: liveFishTransaction?.date ? dateReader(liveFishTransaction.date) : new Date(),
    isLocked: liveFishTransaction?.isLocked,
  };

  const formData = {
    ...initialFormData,
    ...inputMenuContext?.state.formData,
  } satisfies IFormData;

  const availableFishGroup = useGetAvailableFishGroup({
    cageId: formData?.cageId ?? '',
    date: formData?.date ?? new Date(),
    fetch: Boolean(!liveFishTransaction?.fromCompanyId),
  });

  const {data: cages} = useGetSiteCages({resourceId: formData.siteId});

  if (!inputMenuContext) {
    return null;
  }

  const {state, dispatch} = inputMenuContext;

  const isFormValid = !formData || checkFormValidity(formData);

  function updateFormState(payload: Partial<IFormData>) {
    dispatch({
      ...state,
      type: inputMenuActionType.setFormData,
      formData: {...state.formData, ...payload},
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData) {
        throw new Error('Form data not valid');
      }

      const {id, cageId, date, toCompanyId, isLocked = true} = formData;

      if (!planId || !cageId || !date || !toCompanyId) {
        throw new Error('Form data not valid');
      }

      const response = id
        ? await backendCommunicationInstance.updateLiveFishTransaction(id, {
            planId,
            date: localDateSender(date),
            toCompanyId,
            isLocked,
          })
        : await backendCommunicationInstance.addLiveFishTransaction({
            planId,
            cageId,
            date: localDateSender(date),
            toCompanyId,
            isLocked,
          });

      void mutate<LiveFishTransactionResponse[]>(
        (key) => Array.isArray(key) && key.includes(SWRKeyEnum.GET_PLAN_LIVE_FISH_TRANSACTIONS),
        (cachedTransactions) =>
          cachedTransactions &&
          (cachedTransactions.some((cachedTransaction) => cachedTransaction.id === response.data.id)
            ? cachedTransactions.map((cachedTransaction) =>
                cachedTransaction.id === response.data.id ? response.data : cachedTransaction,
              )
            : [...cachedTransactions, response.data]),
      );

      void mutateAll(SWRKeyEnum.GET_SITE_CAGE_FORECAST);
      void mutateAll(SWRKeyEnum.GET_PLANS_HARVEST_VOLUME);
      void mutateAll(SWRKeyEnum.GET_SITE_ACTIONS);
      void mutateAll(SWRKeyEnum.GET_PLAN_ACTIONS);
      void mutateAll(SWRKeyEnum.GET_CAGE_LIVE_FISH_TRANSACTIONS);
      void mutateAll(SWRKeyEnum.GET_MAB_UTILIZATION_PER_CONCESSION);
      void mutateAll(SWRKeyEnum.GET_PLAN_FISH_GROUPS);

      dispatch({type: inputMenuActionType.closeMenu});
    } catch (error) {
      Alert.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Stack component="form" style={{width: '100%'}} spacing={2} onSubmit={handleSubmit}>
      {/* Site Picker */}
      <GenericSelect
        label={t('site')}
        name="siteId"
        disabled={state.method === methodType.edit}
        value={formData?.siteId ?? ''}
        onChange={(event: SelectChangeEvent<unknown>) => {
          const siteId = event.target.value;

          updateFormState({
            siteId: typeof siteId === 'string' ? siteId : '',
            cageId: '',
          });
        }}
        aria-label={t('site')}
        data-testid="site"
      >
        <GenericSelectItem value={undefined}>
          <em>{t('noneSelected')}</em>
        </GenericSelectItem>
        {sites?.map((site) => (
          <GenericSelectItem value={site.id} key={site.id}>
            {site.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>

      {/* Cage Picker */}
      <GenericSelect
        name="cageId"
        disabled={state.method === methodType.edit}
        value={formData?.cageId ?? ''}
        onChange={(event: SelectChangeEvent<unknown>) => {
          const cageId = event.target.value;
          if (typeof cageId !== 'string') {
            return;
          }

          updateFormState({cageId});
        }}
        label={t('cage')}
        aria-label={t('cage')}
        data-testid="cage"
      >
        {cages?.map((cage) => (
          <GenericSelectItem key={cage.id} value={cage.id}>
            {cage.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>

      {/* Date */}
      <InputMenuDatePicker
        name="date"
        value={formData?.date}
        onChange={(date) => {
          if (!date) {
            return;
          }

          updateFormState({date});
        }}
      />

      {/* From company */}
      <CompanySelector
        disabled={true}
        name="fromCompanyId"
        label={t('fromCompany')}
        planId={planId}
        companyId={
          state.method === methodType.add
            ? (availableFishGroup?.company.id ?? formData?.fromCompanyId ?? '')
            : (formData?.fromCompanyId ?? '')
        }
        setCompanyId={(companyId: string) => {
          updateFormState({fromCompanyId: companyId});
        }}
        data-testid="from-company-selector"
      />

      {/* To company */}
      <CompanySelector
        name="toCompanyId"
        label={t('toCompany')}
        planId={planId}
        companyId={formData?.toCompanyId ?? ''}
        setCompanyId={(companyId: string) => {
          updateFormState({toCompanyId: companyId});
        }}
        disabledCompanyIds={[availableFishGroup?.company.id ?? formData?.fromCompanyId ?? '']}
        data-testid="to-company-selector"
      />

      {/* Locked */}
      <FormControlLabel
        checked={formData?.isLocked ?? true}
        onChange={(_, checked) => {
          updateFormState({isLocked: checked});
        }}
        control={<Switch />}
        label={t('locked')}
      />

      <Stack direction="row" spacing={4}>
        <Button
          type="submit"
          variant="contained"
          endIcon={<CheckIcon />}
          fullWidth={true}
          disabled={!isFormValid}
          loading={isLoading}
          color="primary"
          data-testid="input-live-fish-transaction-button-confirm"
        >
          {t('confirm')}
        </Button>
        <Button
          variant="contained"
          endIcon={<CloseIcon />}
          fullWidth={true}
          disabled={isLoading}
          color="secondary"
          data-testid="input-live-fish-transaction-button-cancel"
          onClick={() => {
            dispatch({type: inputMenuActionType.closeMenu});
          }}
        >
          {t('cancel')}
        </Button>
      </Stack>
    </Stack>
  );
};

function checkFormValidity(formData: IFormData) {
  return formData?.siteId && formData?.cageId && formData?.date && formData?.toCompanyId;
}
